import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Custom styles for swiper
const swiperStyles = `
  .swiper-pagination-bullet {
    background: rgba(255, 255, 255, 0.5) !important;
    width: 12px !important;
    height: 12px !important;
    margin: 0 6px !important;
  }
  .swiper-pagination-bullet-active {
    background: white !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = swiperStyles;
  document.head.appendChild(styleSheet);
}

const AboutUs = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const investmentThemes = [
    {
      id: 1,
      title: "Insurance",
      content: "Revolutionary insurance technology solutions that streamline claims processing, improve risk assessment, and enhance customer experience through digital innovation."
    },
    {
      id: 2,
      title: "Wealth",
      content: "Next-generation wealth management platforms that democratize investment opportunities and provide sophisticated portfolio management tools for modern investors."
    },
    {
      id: 3,
      title: "Asset Management",
      content: "Advanced asset management solutions leveraging AI and machine learning to optimize investment strategies and deliver superior returns for institutional and retail clients."
    },
    {
      id: 4,
      title: "Property",
      content: "Innovative proptech solutions that transform real estate investment, property management, and transactions through digital platforms and smart building technologies."
    },
    {
      id: 5,
      title: "Climate & Sustainability",
      content: "Sustainable finance and climate tech investments that drive environmental impact while generating strong financial returns through green technology and ESG-focused solutions."
    }
  ];

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const programOutcomes = [
    {
      id: 1,
      title: "Build a Team",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Simple person icon - minimal */}
          <circle cx="50" cy="30" r="8" strokeWidth="1.5"/>
          <path d="M50 40c-12 0-20 6-20 15v20h40V55c0-9-8-15-20-15z" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      id: 2,
      title: "Grow Your Startup",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Square container */}
          <rect x="25" y="25" width="50" height="50" strokeWidth="1.5"/>
          {/* Cross lines */}
          <path d="M50 25v50M25 50h50" strokeWidth="1.5"/>
          {/* Arrow in top right quadrant */}
          <path d="M55 35l10 10M65 35v10M55 35h10" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      id: 3,
      title: "Unlock Partnerships",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Padlock shackle (open) */}
          <path d="M35 45V30c0-8 7-15 15-15s15 7 15 15v10" strokeWidth="1.5"/>
          {/* Padlock body */}
          <rect x="30" y="45" width="40" height="30" rx="3" strokeWidth="1.5"/>
          {/* Checkmark inside padlock */}
          <path d="M42 57l5 5 8-8" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      id: 4,
      title: "Secure Funding",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Dollar sign in circle */}
          <circle cx="50" cy="50" r="25" strokeWidth="1.5"/>
          <path d="M50 35v30M43 40c0-3 3-5 7-5s7 2 7 5M57 60c0 3-3 5-7 5s-7-2-7-5" strokeWidth="1.5"/>
          {/* Geometric accent lines */}
          <path d="M25 25l10 10M75 25l-10 10M25 75l10-10M75 75l-10-10" strokeWidth="1"/>
        </svg>
      )
    },
    {
      id: 5,
      title: "Scale Operations",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Ascending bars/chart */}
          <rect x="20" y="60" width="8" height="20" strokeWidth="1.5"/>
          <rect x="35" y="50" width="8" height="30" strokeWidth="1.5"/>
          <rect x="50" y="40" width="8" height="40" strokeWidth="1.5"/>
          <rect x="65" y="30" width="8" height="50" strokeWidth="1.5"/>
          {/* Arrow pointing up */}
          <path d="M75 25l5 5M75 25l-5 5M75 25v15" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      id: 6,
      title: "Market Validation",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Target with checkmark */}
          <circle cx="50" cy="50" r="25" strokeWidth="1.5"/>
          <circle cx="50" cy="50" r="15" strokeWidth="1"/>
          <circle cx="50" cy="50" r="8" strokeWidth="1"/>
          {/* Checkmark in center */}
          <path d="M44 50l4 4 8-8" strokeWidth="1.5"/>
          {/* Cross lines */}
          <path d="M25 50h50M50 25v50" strokeWidth="0.5"/>
        </svg>
      )
    },
    {
      id: 7,
      title: "Launch Product",
      icon: (
        <svg className="w-56 h-56 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 100 100" strokeWidth="1.5">
          {/* Rocket ship */}
          <path d="M50 20v45M45 30l10 0M40 40l20 0M35 50l30 0" strokeWidth="1.5"/>
          <path d="M50 20l-8 15h16z" strokeWidth="1.5"/>
          {/* Flames */}
          <path d="M45 65v8M50 65v10M55 65v8" strokeWidth="1.5"/>
          {/* Stars */}
          <circle cx="30" cy="30" r="1" fill="currentColor"/>
          <circle cx="70" cy="35" r="1" fill="currentColor"/>
          <circle cx="25" cy="55" r="1" fill="currentColor"/>
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Hero Section */}
      <section data-hero="true" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <span className="text-white text-sm font-semibold tracking-wider uppercase opacity-90">
              ABOUT US
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight font-AfacadFluxLight max-w-5xl">
            We're venture builders and early stage investors. Our mission is to power founders to go further, faster.
          </h1>
        </div>
      </section>

      {/* Three Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-16">
            <span className="text-gray-300 text-sm font-semibold tracking-wider uppercase mb-4 block">
              OUR APPROACH
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 font-AfacadFluxLight max-w-5xl">
              We partner with entrepreneurs to develop new ideas, technologies and business models into high growth companies.
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-800/30 rounded-2xl p-8 border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
              <div className="text-4xl font-bold text-purple-300 mb-6 font-AfacadFluxLight">
                01
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Build with serial founders & operators
              </h3>
              <p className="text-gray-300  font-ExcentraPro font-light leading-relaxed">
                Our team have taken startups from idea to $1bn IPO. We apply our 
                knowledge & playbooks, de-risking your venture and increasing the odds 
                of success.
              </p>
            </div>
            
            <div className="bg-slate-800/30 rounded-2xl p-8 border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
              <div className="text-4xl font-bold text-purple-300 mb-6 font-AfacadFluxLight">
                02
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Fast-track to product/market fit
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Collaborate with a dedicated team of designers, marketers and engineers 
                to validate concepts, build an MVP and acquire first customers.
              </p>
            </div>
            
            <div className="bg-slate-800/30 rounded-2xl p-8 border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
              <div className="text-4xl font-bold text-purple-300 mb-6 font-AfacadFluxLight">
                03
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Raise capital & build your company
              </h3>
              <p className="text-gray-300 leading-relaxed">
                We help you get funded and recruit early hires - continuing to support as 
                you evolve from concept to operational company.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <span className="text-gray-300 text-sm font-semibold tracking-wider uppercase mb-4 block">
                OUR VALUES
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight">
                Our team come from a variety of backgrounds, beliefs, and ways of looking at the world.
              </h2>
            </div>
            
            {/* Right column - Values list */}
            <div className="space-y-8">
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">01</div>
                <h3 className="text-xl font-bold text-white mb-2">Think big, act small, move fast</h3>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">02</div>
                <h3 className="text-xl font-bold text-white mb-2">Value momentum over perfection</h3>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">03</div>
                <h3 className="text-xl font-bold text-white mb-2">Embrace uncertainty & remain decisive</h3>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">04</div>
                <h3 className="text-xl font-bold text-white mb-2">Suggest solutions not problems</h3>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">05</div>
                <h3 className="text-xl font-bold text-white mb-2">Prioritise diversity to strengthen our collective intelligence</h3>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-purple-300 mb-2 font-AfacadFluxLight">06</div>
                <h3 className="text-xl font-bold text-white mb-2">Be polite, generous and show empathy to others</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Outcomes Swiper Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="text-purple-300 text-sm font-semibold tracking-wider uppercase mb-4 block">
              PROGRAM OUTCOMES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 font-AfacadFluxLight max-w-4xl mx-auto leading-tight">
              For six months, our team operate as co-founders, driving results in mission critical areas
            </h2>
          </div>

          {/* Swiper */}
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={50}
              slidesPerView={1}
              centeredSlides={true}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              navigation={{
                nextEl: '.swiper-button-next-custom',
                prevEl: '.swiper-button-prev-custom',
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination-custom',
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 30,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 40,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 50,
                },
              }}
              className="h-96"
            >
              {programOutcomes.map((outcome) => (
                <SwiperSlide key={outcome.id}>
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    {/* Icon */}
                    <div className="mb-2 flex items-center justify-center">
                      {outcome.icon}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl md:text-3xl font-bold text-white font-AfacadFluxLight">
                      {outcome.title}
                    </h3>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Navigation Buttons */}
            <div className="swiper-button-prev-custom absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 cursor-pointer transition-all duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-custom absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 cursor-pointer transition-all duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Custom Pagination */}
            <div className="swiper-pagination-custom flex justify-center mt-8"></div>
          </div>
        </div>
      </section>

      {/* Financial Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight mb-8">
                Building and backing the future of financial services
              </h2>
            </div>
            
            {/* Right column - Content */}
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                Financial services undergo regular cycles of innovation and change, none more so than in 
                the last few decades. The internet heralded the rise of <span className="text-purple-300 font-medium">mobile banking and digital payments</span>, 
                while recent innovations have seen a boom in popularity of <span className="text-purple-300 font-medium">cryptoassets and blockchain</span>, 
                buy-now-pay-later, and neobanks.
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                All this is underpinned by the building blocks of fintech—<span className="text-purple-300 font-medium">payment technology, open banking</span>, 
                KYC and AML technology—which have made <span className="text-purple-300 font-medium">building fintech products faster and easier</span> 
                than ever.
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                We aspire to be at the forefront of fintech innovation, discovering and backing entrepreneurs 
                who are building the infrastructure of our current and future financial needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Themes Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 font-AfacadFluxLight">
            Investment themes
          </h2>
          
          <div className="space-y-0">
            {investmentThemes.map((theme, index) => (
              <div key={theme.id} className="border-b border-slate-700/50">
                <button
                  onClick={() => toggleAccordion(theme.id)}
                  className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
                >
                  <h3 className="text-xl font-medium text-white group-hover:text-purple-300 transition-colors duration-200">
                    {theme.title}
                  </h3>
                  <div className="ml-4">
                    <svg
                      className={`w-5 h-5 text-white transition-transform duration-200 ${
                        openAccordion === theme.id ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openAccordion === theme.id ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-300 leading-relaxed">
                    {theme.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
